import { defineAsyncComponent } from 'vue';
import { GlBadge, GlLink, GlIcon, GlLabel, GlFormCheckbox, GlSprintf, GlTooltipDirective, GlSkeletonLoader } from '@gitlab/ui';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import { STATUS_OPEN, STATUS_CLOSED } from '~/issues/constants';
import { isScopedLabel } from '~/lib/utils/common_utils';
import { isExternal, visitUrl } from '~/lib/utils/url_utility';
import { __, n__, sprintf } from '~/locale';
import IssuableAssignees from '~/issuable/components/issue_assignees.vue';
import StatusBadge from '~/issuable/components/status_badge.vue';
import SafeHtml from '~/vue_shared/directives/safe_html';
import { titleInLinkSafeHtmlConfig } from '~/lib/dompurify';
import timeagoMixin from '~/vue_shared/mixins/timeago';
import glFeatureFlagMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import WorkItemTypeIcon from '~/work_items/components/work_item_type_icon.vue';
import WorkItemPrefetch from '~/work_items/components/work_item_prefetch.vue';
import { METADATA_KEYS, STATE_CLOSED, STATE_OPEN, WORK_ITEM_TYPE_ROUTE_WORK_ITEM } from '~/work_items/constants';
import { findAssigneesWidget, findLabelsWidget, findLinkedItemsWidget, canRouterNav } from '~/work_items/utils';
import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
export default {
    name: 'IssuableItem',
    components: {
        GlBadge,
        GlLink,
        GlIcon,
        GlLabel,
        GlFormCheckbox,
        GlSprintf,
        GlSkeletonLoader,
        StatusBadge,
        IssuableAssignees,
        WorkItemTypeIcon,
        WorkItemPrefetch,
        WorkItemRelationshipIcons: defineAsyncComponent(()=>import('~/work_items/components/shared/work_item_relationship_icons.vue'))
    },
    directives: {
        GlTooltip: GlTooltipDirective,
        SafeHtml
    },
    mixins: [
        timeagoMixin,
        glFeatureFlagMixin(),
        glSlotsMixin
    ],
    inject: {
        isGroup: {
            default: false
        },
        getWorkItemTypeConfiguration: {
            default: null
        }
    },
    props: {
        hasScopedLabelsFeature: {
            type: Boolean,
            required: false,
            default: false
        },
        issuableSymbol: {
            type: String,
            required: true
        },
        fullPath: {
            type: String,
            required: false,
            default: null
        },
        issuable: {
            type: Object,
            required: true
        },
        labelFilterParam: {
            type: String,
            required: false,
            default: 'label_name'
        },
        showCheckbox: {
            type: Boolean,
            required: true
        },
        checked: {
            type: Boolean,
            required: false,
            default: false
        },
        showWorkItemTypeIcon: {
            type: Boolean,
            required: false,
            default: false
        },
        isActive: {
            type: Boolean,
            required: false,
            default: false
        },
        preventRedirect: {
            type: Boolean,
            required: false,
            default: false
        },
        detailLoading: {
            type: Boolean,
            required: false,
            default: false
        },
        hiddenMetadataKeys: {
            type: Array,
            required: false,
            default: ()=>[]
        }
    },
    emits: [
        'checked-input',
        'select-issuable'
    ],
    titleInLinkSafeHtmlConfig,
    constants: {
        METADATA_KEYS
    },
    computed: {
        issuableId () {
            return getIdFromGraphQLId(this.issuable.id) || this.issuable.id;
        },
        issuableIid () {
            return this.issuable.iid;
        },
        workItemType () {
            return this.issuable.workItemType?.name;
        },
        workItemTypeIconName () {
            return this.issuable.workItemType?.iconName;
        },
        workItemFullPath () {
            return this.issuable.namespace?.fullPath || this.issuable.reference?.split(this.issuableSymbol)[0];
        },
        workItemConfig () {
            // Legacy applications may not have the configuration getter available
            return this.getWorkItemTypeConfiguration ? this.getWorkItemTypeConfiguration(this.workItemType) : {};
        },
        author () {
            return this.issuable.author || {};
        },
        externalAuthor () {
            return this.issuable.externalAuthor;
        },
        issuableLinkHref () {
            return this.issuable.webPath || this.issuable.gitlabWebUrl || this.issuable.webUrl;
        },
        authorId () {
            return getIdFromGraphQLId(this.author.id);
        },
        isIssuableUrlExternal () {
            return isExternal(this.issuableLinkHref ?? '');
        },
        reference () {
            return this.issuable.reference || `${this.issuableSymbol}${this.issuable.iid}`;
        },
        type () {
            return this.issuable.type || this.workItemType;
        },
        labels () {
            return this.issuable.labels?.nodes || this.issuable.labels || findLabelsWidget(this.issuable)?.labels?.nodes || [];
        },
        labelIdsString () {
            return JSON.stringify(this.labels.map((label)=>getIdFromGraphQLId(label.id)));
        },
        assignees () {
            return this.issuable.assignees?.nodes || this.issuable.assignees || findAssigneesWidget(this.issuable)?.assignees?.nodes || [];
        },
        linkedItemsWidget () {
            return findLinkedItemsWidget(this.issuable);
        },
        blockingCount () {
            return this.linkedItemsWidget?.blockingCount || 0;
        },
        blockedByCount () {
            return this.linkedItemsWidget?.blockedByCount || 0;
        },
        hasBlockingRelationships () {
            return this.blockingCount > 0 || this.blockedByCount > 0;
        },
        createdAt () {
            return this.issuable.createdAt ? this.timeFormatted(this.issuable.createdAt) : undefined;
        },
        isOpen () {
            return [
                STATUS_OPEN,
                STATE_OPEN
            ].includes(this.issuable.state);
        },
        isClosed () {
            return [
                STATUS_CLOSED,
                STATE_CLOSED
            ].includes(this.issuable.state);
        },
        statusTooltip () {
            return this.issuable.mergedAt ? this.tooltipTitle(this.issuable.mergedAt) : '';
        },
        timestamp () {
            return this.isClosed && this.issuable.closedAt ? this.issuable.closedAt : this.issuable.updatedAt;
        },
        issuableTitleProps () {
            if (this.isIssuableUrlExternal) {
                return {
                    target: '_blank'
                };
            }
            return {};
        },
        taskStatus () {
            const { completedCount, count } = this.issuable.taskCompletionStatus || {};
            if (!count) {
                return undefined;
            }
            return sprintf(n__('%{completedCount} of %{count} checklist item completed', '%{completedCount} of %{count} checklist items completed', count), {
                completedCount,
                count
            });
        },
        notesCount () {
            return this.issuable.userDiscussionsCount ?? this.issuable.userNotesCount;
        },
        showDiscussions () {
            return typeof this.notesCount === 'number';
        },
        showIssuableMeta () {
            return Boolean(this.hasSlotContents('status') || this.hasSlotContents('statistics') || this.showDiscussions || this.issuable.assignees);
        },
        showDraftStatusBadge () {
            return Boolean(this.isMergeRequest && this.isOpen && this.issuable.draft && this.glFeatures.showMergeRequestStatusDraft);
        },
        statusBadgeVariant () {
            if (this.isMergeRequest && this.isClosed) {
                return 'danger';
            }
            return 'info';
        },
        isMergeRequest () {
            // eslint-disable-next-line no-underscore-dangle
            return this.issuable.__typename === 'MergeRequest';
        },
        useIssueView () {
            return this.workItemConfig?.useIssueView;
        },
        useWorkItemTemplate () {
            if (this.isGroup) return false;
            return !this.workItemConfig?.useIssueView;
        },
        hiddenIssuableTitle () {
            if (this.isMergeRequest) {
                return __('This merge request is hidden because its author has been banned.');
            }
            return __('This issue is hidden because its author has been banned.');
        },
        listItemUniqueId () {
            const availableFullPath = this.workItemFullPath || this.fullPath;
            return `listItem-${availableFullPath}/${getIdFromGraphQLId(this.issuable.id)}`;
        },
        isClickableLink () {
            return this.preventRedirect && !this.showCheckbox;
        }
    },
    methods: {
        hasSlotContents (slotName) {
            return Boolean(this.glSlots()[slotName]);
        },
        scopedLabel (label) {
            const allowsScopedLabels = this.hasScopedLabelsFeature || findLabelsWidget(this.issuable)?.allowsScopedLabels;
            return allowsScopedLabels && isScopedLabel(label);
        },
        labelTitle (label) {
            return label.title || label.name;
        },
        labelFooter (label) {
            return label.archived ? __('Archived') : '';
        },
        labelTarget (label) {
            const value = encodeURIComponent(this.labelTitle(label));
            return `?${this.labelFilterParam}[]=${value}`;
        },
        handleIssuableItemClick (e) {
            if (e.metaKey || e.ctrlKey || e.shiftKey || this.showCheckbox || e.button === 1) {
                return;
            }
            e.preventDefault();
            // Unsupported types incidents, tickets, and test cases should not open in drawer
            if (this.useIssueView || !this.preventRedirect) {
                this.navigateToIssuable();
                return;
            }
            if (this.isActive) {
                this.$emit('select-issuable', null);
            } else {
                this.$emit('select-issuable', {
                    id: this.issuable.id,
                    iid: this.issuableIid,
                    webUrl: this.issuable.webUrl,
                    fullPath: this.workItemFullPath,
                    workItemType: this.type.toLowerCase()
                });
            }
        },
        navigateToIssuable () {
            if (!this.fullPath) {
                visitUrl(this.issuableLinkHref);
                return;
            }
            const shouldRouterNav = canRouterNav({
                fullPath: this.fullPath,
                webUrl: this.issuableLinkHref,
                isGroup: this.isGroup,
                issueAsWorkItem: this.useWorkItemTemplate
            });
            if (shouldRouterNav) {
                this.$router.push({
                    name: 'workItem',
                    params: {
                        iid: this.issuableIid,
                        type: WORK_ITEM_TYPE_ROUTE_WORK_ITEM
                    }
                });
            } else {
                visitUrl(this.issuableLinkHref);
            }
        },
        handleRowClick (e) {
            if (this.preventRedirect) {
                this.handleIssuableItemClick(e);
            }
        }
    }
};
